import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { transformContent, transformImages } from "../../../lib/pageHelper";

import GoogleMap2 from "../GoogleMap2";
import { attachSharedMap, detachSharedMap, initSharedMap } from "../../../lib/sharedMap";

export default function PageContent({ page }) {
  const [ isLoaded, setIsLoaded ] = useState(false);
  const [ newPageContent, setNewPageContent ] = useState();
  const [ mapVisible, setMapVisible ] = useState(false);
  const pageMapTargetRef = useRef(null);

  const isValidCoord = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    const n = parseFloat(v);
    return Number.isFinite(n);
  };

  useEffect(() => {
    if (newPageContent) {
      var imgs = document.querySelectorAll(".post-content img");
      imgs.forEach.call(imgs, function (element, index, array) {
        const floatDir = element.classList.contains("alignleft")
          ? "alignleft"
          : element.classList.contains("alignright")
          ? "alignright"
          : "aligncenter";
        element.setAttribute("loading", "lazy");
        setTimeout(function () {
          const parent = element.closest("span");
          if (parent) {
            const grandParent = parent.closest(".blue-bg-wrap");
            if (grandParent) {
              grandParent.classList.add("animated");
              if (floatDir) grandParent.classList.add(floatDir);

              parent.classList.add("animated");
              setTimeout(function () {
                element.classList.add("animated");
              }, 100);
            }
          }
        }, 150);
      });
    }
  }, [ newPageContent ]);

  useEffect(() => {
    if (page && page?.content) {
      const newPageContent = transformImages(page.content);
      newPageContent.replace("<p></p>", "");
      setNewPageContent(newPageContent);
      
      // Ensure the map (and other immediate content) mounts right away,
      // but keep the animated class addition delayed for the entrance effect.
      setIsLoaded(true);
      // page content prepared
      try {
        // page prepared
      } catch (e) {}
      // If the page provides googleMaps after client-side navigation
      // dispatch a small event to allow map components to reflow once
      // entrance animations have completed. Hard refresh always works
      // because the container is visible immediately; this helps the
      // map recover when data arrives later.
      if (isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude)) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event("aeon:map:reflow"));
          } catch (e) {}
        }, 600);
      }
      setTimeout(function () {
        const hiddenContent = document.getElementById("page-content");
        if (hiddenContent) hiddenContent.classList.add("animated");
      }, 100);
    }
  }, [ page ]);

  // reset map visible when page changes
  useEffect(() => {
    setMapVisible(false);
  }, [ page?.slug ]);

  useEffect(() => {
    try {
      // map visible state changed
    } catch (e) {}
  }, [ mapVisible ]);

  // Attach/detach the shared map to the page's placeholder when googleMaps are present
  useEffect(() => {
    let attached = false;
    const has = isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude);
    const attach = async () => {
      try {
        await initSharedMap({ center: { lat: Number(page?.googleMaps?.latitude || 0), lng: Number(page?.googleMaps?.longitude || 0) }, zoom: Number(page?.googleMaps?.zoom || 8) });
        const target = pageMapTargetRef.current;
        if (has && target) {
          await attachSharedMap(target, { latitude: page?.googleMaps?.latitude, longitude: page?.googleMaps?.longitude, zoom: page?.googleMaps?.zoom });
          setMapVisible(true);
          attached = true;
        }
      } catch (e) {
        // silent
      }
    };

    attach();

    return () => {
      if (attached) {
        try {
          detachSharedMap();
        } catch (e) {}
      }
      attached = false;
      setMapVisible(false);
    };
  }, [ page?.slug, page?.googleMaps?.latitude, page?.googleMaps?.longitude ]);

  // Detect when googleMaps becomes available after client-side navigation
  // (falsy -> truthy) and dispatch the reflow event so maps that were
  // previously absent can be reflowed when the data arrives.
  const _prevHasGoogleMaps = useRef(isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude));
  useEffect(() => {
    const has = isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude);
    if (!_prevHasGoogleMaps.current && has) {
      setTimeout(() => {
        try {
          window.dispatchEvent(new Event("aeon:map:reflow"));
        } catch (e) {
          // silent
        }
      }, 600);
    }
    _prevHasGoogleMaps.current = has;
  }, [ page?.googleMaps?.latitude, page?.googleMaps?.longitude ]);

  // polling fallback removed — rely on transition detection and prefetching

  const StaticArrow = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={4}
      stroke="#33a1e7"
      className={`blue w-3 h-3 ml-[1px]`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );

  const HoverArrow = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={4}
      stroke="#33a1e7"
      className={`trans-ease-all opacity-0 group-hover:opacity-100 w-3 h-3 ml-[-6px]`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );

  useEffect(() => {
    const hiddenContent = document.getElementById("page-content");
    if (hiddenContent) {
      setTimeout(() => {
        hiddenContent.classList.add("animated");
      }, 50);
    }
    // Always dispatch a reflow after the page mounts so any map components
    // have a final opportunity to resize/pan even if data or layout timing
    // prevented earlier attempts.
    setTimeout(() => {
      try {
        window.dispatchEvent(new Event("aeon:map:reflow"));
      } catch (e) {
        // silent
      }
    }, 350);
  }, [ page, newPageContent ]);

  return (
    <article className="flex pb-4">
      {/* development overlay removed */}
      {page && newPageContent && (
        <div
          id="page-content"
          className="hideme w-full pt-6 min-[768px]:pt-8 px-4 min-[992px]:px-8 min-[1366px]:pt-10 z-50"
        >
          <h1 className="text-center min-[768px]:text-left font-frutiger-light uppercase font-bold text-[#24292e] mb-2 min-[768px]:mb-6 ">
            {page.title}
          </h1>
          {(page?.maybeHasMap || (isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude))) && isLoaded && (
            <div className="map_block animated">
              <div
                id="map_canvas"
                className="animated"
                style={{ position: "relative", opacity: mapVisible ? 1 : undefined, visibility: mapVisible ? 'visible' : undefined }}
              >
                {!mapVisible && (
                  <div style={{ height: "360px", width: "100%", background: "#e9f7fb", transition: "opacity 600ms ease, transform 600ms ease", opacity: mapVisible ? 0 : 1, border: "3px solid #33A0D6", borderRadius: 6, boxShadow: "0 6px 0 rgba(0,0,0,0.08)", pointerEvents: 'none' }} />
                )}
                <div style={{ transition: "opacity 600ms ease, transform 600ms ease", opacity: mapVisible ? 1 : 0, transform: mapVisible ? 'scale(1)' : 'scale(0.996)' }}>
                  {isValidCoord(page?.googleMaps?.latitude) && isValidCoord(page?.googleMaps?.longitude) ? (
                    <div ref={pageMapTargetRef} style={{ width: '100%' }} />
                  ) : null}
                </div>
              </div>
            </div>
          )}
          <div className="post-content mt-3 xl:mt-4 text-justify">
            {transformContent(newPageContent)}
          </div>
        </div>
      )}
      {page && page?.slug === "career-opportunities" && (
        <div
          id="career-opportunities-blue"
          className="mt-1 mt-md-3 mb-4 mt-lg-5 career-opportunities-wrapper"
        >
          <Link
            className="w-fit mt-2 trans-ease-all arrow-link group flex items-center uppercase text-sm font-semibold  hover:text-[#0056b3]  blue border-transparent hover:border-[#33a1e7] border-b-2"
            title="Career Opportunities at AEON"
            to="/career-opportunities"
          >
            <span className="">LEARN MORE</span>
            <StaticArrow />
            <HoverArrow />
          </Link>
        </div>
      )}
    </article>
  );
}
